import { Field, Input, Label } from "@headlessui/react";
import KeyValuePair from "../../interfaces/keyValuePair";
import RequiredField from "../../components/RequiredField";
import MultiSelect from "../peerReviewGroups/MultiSelect";
import { AddEditBroadcastAlertFormViewModel } from "./alertsApiSlice";
import { getSchoolDisplayName } from "./AdminAlerts";

export interface BroadcastAlertFormData {
	message: string;
	beginDate: string;
	expireDate: string;
	districtIds: number[];
	schoolIds: number[];
	roleValues: string[];
}

interface AddEditBroadcastAlertForm {
	viewModel: AddEditBroadcastAlertFormViewModel;
	formData: BroadcastAlertFormData;
	updateFormData: <K extends keyof BroadcastAlertFormData>(
		field: K,
		value: BroadcastAlertFormData[K]
	) => void;
}

export function AddEditBroadcastAlertForm({
	viewModel,
	formData,
	updateFormData,
}: AddEditBroadcastAlertForm) {
	const today = new Date().toISOString().split("T")[0];

	const districtPool: KeyValuePair<number, string>[] = viewModel.districts.map((d) => ({
		key: Number(d.value),
		value: d.text,
	}));

	const schoolPool: KeyValuePair<number, string>[] = viewModel.schools.map((s) => ({
		key: Number(s.id),
		value: getSchoolDisplayName(s, viewModel.schools),
	}));

	const rolePool: KeyValuePair<number, string>[] = viewModel.roles.map((r, i) => ({
		key: i,
		value: r.text,
	}));

	const selectedRoles = rolePool.filter((r) =>
		formData.roleValues?.includes(String(viewModel.roles[r.key].value))
	);

	const selectedDistricts = districtPool.filter((d) => formData.districtIds?.includes(d.key));

	const selectedSchools = schoolPool.filter((s) => formData.schoolIds?.includes(s.key));

	const handleRolesChange = (selected: KeyValuePair<number, string>[]) => {
		updateFormData(
			"roleValues",
			selected.map((r) => String(viewModel.roles[r.key].value))
		);
	};

	const handleDistrictsChange = (selected: KeyValuePair<number, string>[]) => {
		updateFormData(
			"districtIds",
			selected.map((d) => d.key)
		);
	};

	const handleSchoolsChange = (selected: KeyValuePair<number, string>[]) => {
		updateFormData(
			"schoolIds",
			selected.map((s) => s.key)
		);
	};

	return (
		<div className="space-y-2 mt-2">
			<Field>
				<Label as="span" className="font-medium text-sm">
					Message
					<RequiredField />
				</Label>
				<textarea
					id="message"
					value={formData.message ?? ""}
					onChange={(e) => updateFormData("message", e.target.value)}
					className="ring-gray-300 block w-full rounded-lg border-0 text-sm ring-1 ring-inset focus:ring-2 focus:ring-inset focus:ring-primary-600"
					rows={3}
					required
					maxLength={2000}
				/>
			</Field>
			<div className="grid grid-cols-2 gap-4">
				<Field>
					<Label as="span" className="font-medium text-sm">
						Begin Date
					</Label>
					<Input
						type="date"
						name="beginDate"
						id="beginDate"
						className={`focus:ring-primary-600 ring-gray-300 block w-full rounded-lg border-0 text-sm ring-1 ring-inset focus:ring-2 focus:ring-inset`}
						value={formData.beginDate}
						onChange={(e) => updateFormData("beginDate", e.target.value)}
						min={today}
					/>
				</Field>
				<Field>
					<Label as="span" className="font-medium text-sm">
						End Date
						<RequiredField />
					</Label>
					<Input
						type="date"
						name="expireDate"
						id="expireDate"
						className={`focus:ring-primary-600 ring-gray-300 block w-full rounded-lg border-0 text-sm ring-1 ring-inset focus:ring-2 focus:ring-inset`}
						required
						value={formData.expireDate}
						onChange={(e) => updateFormData("expireDate", e.target.value)}
						min={today}
					/>
				</Field>
			</div>
			<Field>
				<Label htmlFor="roles" className="font-medium text-sm">
					Roles
				</Label>
				<MultiSelect
					pool={rolePool}
					selected={selectedRoles}
					className="w-full"
					defaultText="All"
					onSelectedChange={handleRolesChange}
				/>
			</Field>
			<Field>
				<Label htmlFor="districts" className="font-medium text-sm">
					Districts
				</Label>
				<MultiSelect
					pool={districtPool}
					selected={selectedDistricts}
					className="w-full"
					defaultText="All"
					onSelectedChange={handleDistrictsChange}
				/>
			</Field>
			<Field>
				<Label htmlFor="schools" className="font-medium text-sm">
					Schools
				</Label>
				<MultiSelect
					pool={schoolPool}
					selected={selectedSchools}
					className="w-full"
					defaultText="All"
					onSelectedChange={handleSchoolsChange}
				/>
			</Field>
		</div>
	);
}
